import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Plyr from 'plyr';

import { loggerService } from 'shared/lib/logger';
import { PlayCircle } from 'shared/ui/play-circle';

import { defaultVideoPlayerOptions } from './constants';
import { PlyrVideoPlayerProps } from './types';
import styles from './styles.module.scss';

export const PlyrVideoPlayer = ({
  source,
  onPlay,
  onPause,
  onEnd,
  onDestroy,
  containerClassnames,
  options = defaultVideoPlayerOptions,
  playButtonIcon = <PlayCircle className={styles.playIcon} />,
}: PlyrVideoPlayerProps) => {
  const playerRef = useRef<HTMLVideoElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const plyrRef = useRef<Plyr | null>(null);

  const [isPlayButtonShown, showPlayButton] = useState(true);

  const handleLoadedClassnameAdd = (element: HTMLDivElement | null) => {
    if (element) element.classList.add(styles.loaded);
  };

  const handlePlayVideo = () => {
    if (plyrRef.current) {
      const playPromise = plyrRef.current.play();

      if (playPromise) {
        playPromise
          .then(() => {
            showPlayButton(false);
          })
          .catch((error: Error) => {
            loggerService.logError(error);
          });
      }
    }
  };

  // handle poster placeholder showing while poster is being processed (loaded or failed to load)
  useEffect(() => {
    if (!source.poster || !placeholderRef.current) return;
    const img = new Image();
    img.src = source.poster;
    img.onload = () => handleLoadedClassnameAdd(placeholderRef.current);
    img.onerror = () => handleLoadedClassnameAdd(placeholderRef.current);

    return () => {
      // need to hardly clear the video src to remove socket and cancel pending requests
      const video = document.querySelector('video');
      if (video) {
        video.src = '';
      }
    };
  }, [source]);

  // initialize Plyr and save its instance in plyrRef
  useEffect(() => {
    if (playerRef.current) {
      plyrRef.current = new Plyr(playerRef.current, {
        ...defaultVideoPlayerOptions,
        ...options,
      });
    }

    return () => {
      plyrRef.current?.destroy();
      onDestroy?.();
    };
  }, [onDestroy, options]);

  // prevent video autoplaying on component render
  useEffect(() => {
    if (plyrRef.current) {
      plyrRef.current.stop();
      plyrRef.current.source = source;
      showPlayButton(true);
    }
  }, [source]);

  // handle user actions
  useEffect(() => {
    if (plyrRef.current) {
      plyrRef.current.on('pause', () => {
        showPlayButton(true);
        onPause?.(plyrRef.current);
      });

      plyrRef.current.on('play', () => {
        showPlayButton(false);
        onPlay?.(plyrRef.current);
      });

      plyrRef.current.on('ended', () => {
        onEnd?.(plyrRef.current);
      });
    }
  }, [onEnd, onPause, onPlay]);

  return (
    <div className={clsx(styles.playerContainer, containerClassnames)}>
      <video
        preload="none"
        playsInline
        ref={playerRef}
        className={styles.player}
      />
      {isPlayButtonShown && (
        <button
          type="button"
          className={styles.playButton}
          onClick={handlePlayVideo}
        >
          {playButtonIcon}
        </button>
      )}
      {source.poster && (
        <div ref={placeholderRef} className={styles.placeholder} />
      )}
      {source.tracks &&
        source.tracks.map((trackInfo, index) => {
          return (
            <track
              key={index}
              kind={trackInfo.kind}
              label={trackInfo.label}
              src={trackInfo.src}
              srcLang={trackInfo.srcLang}
              default={trackInfo.default}
            />
          );
        })}
    </div>
  );
};
